import { useContext } from 'react'
import { useIntl } from 'react-intl'
import paymentsGlossaryMessages from 'i18n/paymentsGlossaryMessages'
import styled from 'styled-components'
import { rem } from 'polished'

import InterestFreeOption from './InterestFreeOption'
import {
  isKlarnaEnabled,
  isWithinKlarnaAvailabilityRange,
  getKlarnaBpMaxLimit,
  isKlarnaBpEnabled,
  isWithinKlarnaBpAvailabilityRange,
} from 'lib/payment/paymentUtils'
import {
  KLARNA_PAYMENT_TYPE,
  KLARNA_BP_PAYMENT_TYPE,
  HOOLAH_BP_PAYMENT_TYPE,
  ATOME_BP_PAYMENT_TYPE,
  AFTER_PAY_BP_PAYMENT_TYPE,
  WAAVE_BP_PAYMENT_TYPE,
} from 'constants/payment'
import { connect } from 'react-redux'
import { getAfterPayBpMaxLimit, isAfterPayBpEnabled, isWithinAfterPayBpAvailabilityRange } from 'lib/payment/afterPayBpUtils'
import { getHoolahBpMaxLimit, isHoolahBpEnabled, isWithinHoolahBpAvailabilityRange } from 'lib/payment/hoolahBpUtils'
import { getAtomeBpMaxLimit, isAtomeBpEnabled, isWithinAtomeBpAvailabilityRange } from 'lib/payment/atomeBpUtils'
import { isWaaveBpEnabled, isWithinWaaveBpAvailabilityRange } from 'lib/payment/waaveBpUtils'
import { formatRoundedAmount, formatRoundedAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import Modal from 'LuxKit/Modal/Modal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import GeoContext from 'contexts/geoContext'

const HowToUseSection = styled.div`
  margin-inline: auto;
  line-height: 1.8;
  max-width: ${rem(680)};
`

const PaymentOptionSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  > * + * {
    margin-top: ${rem(12)};
  }
`

interface Props {
  isOpen?: boolean
  onClose?: () => void
  currency: string
  offer?: App.Offer
}

function InterestFreeLearnMoreModal(props: Props) {
  const { isOpen, onClose, currency, offer } = props
  const intl = useIntl()

  const { currentRegionCode } = useContext(GeoContext)

  const payOver4Label = intl.formatMessage({
    id: 'K5WRrOlpnfNVzO9ycgZy',
    defaultMessage: 'Pay over 4 interest-free instalments',
  })

  const payOver3Label = intl.formatMessage({
    id: 'Hnxyn-dnKir9t3leKLj7',
    defaultMessage: 'Pay over 3 interest-free instalments',
  })

  const payInThreeLabel = intl.formatMessage({
    id: 'cyhjAsqEuLuv01D2JFBl',
    defaultMessage: 'Pay in three instalments',
  })

  const payViaDirectDebitLabel = intl.formatMessage({
    id: 'ETxWrAE00DO2YQmSEhks',
    defaultMessage: 'Pay securely via direct debit',
  })

  const noMinimumSpendLabel = intl.formatMessage({
    id: 'lF4yVVrmbUL3cHcst_v7',
    defaultMessage: 'No minimum spend.',
  })

  const klarnaPayAlt = intl.formatMessage({
    id: 'a_jEVA9Qap-HR_em4FJs',
    defaultMessage: 'Klarna Pay',
  })
  const afterPayAlt = intl.formatMessage({
    id: 'thEGL02FcoyyTwn1nolf',
    defaultMessage: 'After Pay',
  })
  const shopbackAlt = intl.formatMessage({
    id: '_OA3rfIispl4C0pPpYS_',
    defaultMessage: 'Shopback',
  })
  const atomeAlt = intl.formatMessage({
    id: 'OFr5B0VuBprgejfbGmbz',
    defaultMessage: 'Atome',
  })
  const luxPayAlt = intl.formatMessage(paymentsGlossaryMessages.luxPay)

  const products = [
    {
      id: KLARNA_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-klarna')} alt={klarnaPayAlt} height="40" />,
      brand: 'Klarna',
      title: payOver4Label,
      content: intl.formatMessage({
        id: 'rDfAs856966Zxe_tpoNX',
        defaultMessage: 'For purchases under {amount}',
      }, { amount: formatRoundedAmountWithDollarType(2500, currency, currentRegionCode) }),
      to: '/klarna',
      isAvailable: (currency: string, offer?: App.Offer) => isKlarnaEnabled(currency) && (!offer || offer.packages.some(p => isWithinKlarnaAvailabilityRange(p.price))),
    },
    {
      id: KLARNA_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-klarna')} alt={klarnaPayAlt} height="40" />,
      brand: 'Klarna',
      title: currency === 'GBP' ? payOver3Label : payOver4Label,
      content: intl.formatMessage({
        id: 'VCGxmuXPPpaAnl01eVkg',
        defaultMessage: 'For purchases under {amount}',
      }, { amount: formatRoundedAmount(getKlarnaBpMaxLimit(currency), currency, currentRegionCode) }),
      to: '/klarna',
      isAvailable: (currency: string, offer?: App.Offer) => isKlarnaBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinKlarnaBpAvailabilityRange(p.price, currency))),
    },
    {
      id: AFTER_PAY_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-afterpay')} alt={afterPayAlt} height="40" />,
      brand: 'After pay',
      title: payOver4Label,
      content: intl.formatMessage({
        id: 'WkL-7tjDsjKOKzGjZYnR',
        defaultMessage: 'For purchases under {amount}',
      }, { amount: formatRoundedAmount(getAfterPayBpMaxLimit(currency), currency, currentRegionCode) }),
      to: '/afterpay',
      isAvailable: (currency: string) => isAfterPayBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinAfterPayBpAvailabilityRange(p.price, currency))),
    },
    {
      id: HOOLAH_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-placeholder')} alt={shopbackAlt} height="40" />,
      brand: 'Shopback',
      title: payInThreeLabel,
      content: intl.formatMessage({
        id: 'FSq2LPjQTFlhM9VFIXDi',
        defaultMessage: 'For purchases {amount} and under',
      }, { amount: formatRoundedAmountWithDollarType(getHoolahBpMaxLimit(currency), currency, currentRegionCode) }),
      to: '/shopbackpaylater',
      isAvailable: (currency: string) => isHoolahBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinHoolahBpAvailabilityRange(p.price, currency))),
    },
    {
      id: ATOME_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-placeholder')} alt={atomeAlt} height="40" />,
      brand: 'Atome',
      title: payInThreeLabel,
      content: intl.formatMessage({
        id: 'I9kfsjpTTSlCEA_f_Rx_',
        defaultMessage: 'For purchases {amount} and under',
      }, { amount: formatRoundedAmountWithDollarType(getAtomeBpMaxLimit(currency), currency, currentRegionCode) }),
      to: '/atome',
      isAvailable: (currency: string) => isAtomeBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinAtomeBpAvailabilityRange(p.price, currency))),
    },
    {
      id: WAAVE_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-waave')} alt={luxPayAlt} height="15" />,
      brand: 'LuxPay, powered by Waave',
      title: payViaDirectDebitLabel,
      content: noMinimumSpendLabel,
      to: '/luxpay',
      isAvailable: (currency: string) => isWaaveBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinWaaveBpAvailabilityRange(p.price, currency))),
    },
  ]

  const modalTitle = intl.formatMessage({
    id: 'rpb0XLQ21O09TzHjSafz',
    defaultMessage: 'Interest free payment options',
  })

  const modalSubtitle = intl.formatMessage({
    id: 'hTsYeNd34RWc236LgyAo',
    defaultMessage: 'Select from the following interest free payment options in checkout.',
  })

  const howToUseHeading = intl.formatMessage({
    id: '5k4zvpvWCpE5bbwULvdF',
    defaultMessage: 'How to use this service',
  })

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      subtitle={modalSubtitle}
    >
      <VerticalSpacer gap={20}>
        <PaymentOptionSection>
          {products.map(product => {
            if (product.isAvailable(currency, offer)) {
              return (<InterestFreeOption
                key={product.id}
                product={product}
              />)
            }
            return null
          })}
        </PaymentOptionSection>
        <HowToUseSection>
          <Heading variant="heading6">{howToUseHeading}</Heading>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'nBo-6gDoaUPotvg-wqTx',
              defaultMessage: 'Select your Escape and checkout as normal. In the checkout select the interest free payment option and continue from there.',
            })}
          </BodyText>
        </HowToUseSection>
      </VerticalSpacer>
    </Modal>
  )
}

function mapStateToProps(state: App.State) {
  return {
    currency: state.geo.currentCurrency,
  }
}

export default connect(mapStateToProps)(InterestFreeLearnMoreModal)
